import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FileUpload from '../components/FileUpload';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

export default function Setup() {
  const navigate = useNavigate();
  const [jd, setJd] = useState('');
  const [company, setCompany] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [background, setBackground] = useState('');
  const [sessionLength, setSessionLength] = useState(15);
  const [loading, setLoading] = useState(false);
  const [researching, setResearching] = useState(false);
  const [companyBrief, setCompanyBrief] = useState<any>(null);
  const [error, setError] = useState('');

  const lengths = [5, 10, 15, 20, 30];

  const researchCompany = async () => {
    if (!companyUrl && !company) return;
    setResearching(true);
    setCompanyBrief(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/research/company`,
        { companyWebsite: companyUrl, companyName: company },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyBrief(response.data.brief);
    } catch {
      // Silent fail
    } finally {
      setResearching(false);
    }
  };

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jd || !background) {
      setError('Please paste the job description and your background');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/prepare`,
        {
          rawJD: jd,
          companyName: company,
          companyWebsite: companyUrl,
          candidateInput: background,
          sessionLengthMinutes: sessionLength,
          sessionType: 'interview'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/live-interview', { state: { session: response.data, companyBrief } });
    } catch (err: any) {
      if (err.response?.data?.upgradeRequired) {
        if (window.confirm(err.response.data.error + '\n\nUpgrade to Pro now?')) {
          const token = localStorage.getItem('token');
          const res = await axios.post(`${API_BASE_URL}/api/stripe/create-checkout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          window.location.href = res.data.checkoutUrl;
        }
      } else {
        setError('Failed to prepare session. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <p style={styles.brand}>VIRILITER</p>
        <h1 style={styles.title}>Session Setup</h1>
        <p style={styles.subtitle}>Tell us about the role and yourself.</p>

        <form onSubmit={handleStart} style={styles.form}>
          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.section}>
            <p style={styles.label}>COMPANY NAME</p>
            <input
              style={styles.input}
              type="text"
              placeholder="Company name"
              value={company}
              onChange={e => setCompany(e.target.value)}
            />
          </div>

          <div style={styles.section}>
            <p style={styles.label}>COMPANY WEBSITE <span style={{ color: '#334155', fontWeight: '400' }}>(OPTIONAL)</span></p>
            <div style={styles.urlRow}>
              <input
                style={{ ...styles.input, flex: 1 }}
                type="url"
                placeholder="https://company.com"
                value={companyUrl}
                onChange={e => setCompanyUrl(e.target.value)}
              />
              <button
                type="button"
                style={styles.researchButton}
                onClick={researchCompany}
                disabled={researching || (!companyUrl && !company)}
              >
                {researching ? '...' : 'Research'}
              </button>
            </div>
          </div>

          {companyBrief && (
            <div style={styles.briefCard}>
              <p style={styles.briefTitle}>📋 COMPANY BRIEF</p>
              <div style={styles.briefItem}>
                <p style={styles.briefLabel}>WHAT THEY DO</p>
                <p style={styles.briefText}>{companyBrief.what_they_do}</p>
              </div>
              <div style={styles.briefItem}>
                <p style={styles.briefLabel}>MISSION & VALUES</p>
                <p style={styles.briefText}>{companyBrief.mission_values}</p>
              </div>
              <div style={styles.briefItem}>
                <p style={styles.briefLabel}>RECENT NEWS</p>
                <p style={styles.briefText}>{companyBrief.recent_news}</p>
              </div>
              <div style={styles.briefItem}>
                <p style={styles.briefLabel}>WHY WORK HERE</p>
                <p style={styles.briefText}>{companyBrief.why_work_here}</p>
              </div>
              <div style={{ ...styles.briefItem, borderBottom: 'none', paddingBottom: 0 }}>
                <p style={{ ...styles.briefLabel, color: '#F59E0B' }}>💡 INTERVIEW TIP</p>
                <p style={styles.briefText}>{companyBrief.interview_tip}</p>
              </div>
            </div>
          )}

          <div style={styles.section}>
            <p style={styles.label}>JOB DESCRIPTION</p>
            <FileUpload onExtracted={(text) => setJd(text)} fieldName="Job Description" />
            <textarea
              style={styles.textarea}
              placeholder="Paste the full job description here, or upload above..."
              value={jd}
              onChange={e => setJd(e.target.value)}
              rows={8}
            />
          </div>

          <div style={styles.section}>
            <p style={styles.label}>YOUR BACKGROUND / RESUME</p>
            <FileUpload onExtracted={(text) => setBackground(text)} fieldName="Resume" />
            <textarea
              style={styles.textarea}
              placeholder="Describe your experience, achievements, and background, or upload your resume above..."
              value={background}
              onChange={e => setBackground(e.target.value)}
              rows={5}
            />
          </div>

          <div style={styles.section}>
            <p style={styles.label}>SESSION LENGTH</p>
            <div style={styles.lengths}>
              {lengths.map(l => (
                <button
                  key={l}
                  type="button"
                  style={{ ...styles.lengthButton, ...(sessionLength === l ? styles.lengthActive : {}) }}
                  onClick={() => setSessionLength(l)}
                >
                  <span style={{ color: sessionLength === l ? '#ffffff' : '#475569', fontWeight: '700' }}>{l}m</span>
                </button>
              ))}
            </div>
          </div>

          <div style={styles.disclaimerBox}>
            <p style={styles.disclaimerTitle}>⚠ DISCLAIMER</p>
            <p style={styles.disclaimerText}>
              This is an AI-powered role-play simulation. The interviewer is not affiliated with any real organization. This session is for practice purposes only.
            </p>
          </div>

          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Preparing your session...' : 'Start Live Interview'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', justifyContent: 'center', padding: '40px 24px' },
  card: { width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  title: { color: '#F1F5F9', fontSize: '30px', fontWeight: '800', textAlign: 'center', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { color: '#475569', fontSize: '13px', textAlign: 'center', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '8px' },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { color: '#3B82F6', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', margin: 0 },
  error: { color: '#EF4444', fontSize: '14px', margin: 0 },
  input: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '16px', border: '1px solid #1E293B', outline: 'none', width: '100%', boxSizing: 'border-box' },
  urlRow: { display: 'flex', gap: '8px', alignItems: 'stretch' },
  researchButton: { backgroundColor: '#1E293B', color: '#3B82F6', border: '1px solid #3B82F6', borderRadius: '12px', padding: '0 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  briefCard: { backgroundColor: '#0A1628', borderRadius: '12px', padding: '20px', border: '1px solid #1E3A5F', display: 'flex', flexDirection: 'column', gap: '0' },
  briefTitle: { color: '#3B82F6', fontSize: '10px', letterSpacing: '3px', fontWeight: '700', margin: '0 0 16px 0' },
  briefItem: { borderBottom: '1px solid #1E293B', paddingBottom: '12px', marginBottom: '12px' },
  briefLabel: { color: '#3B82F6', fontSize: '9px', letterSpacing: '2px', fontWeight: '700', margin: '0 0 4px 0' },
  briefText: { color: '#94A3B8', fontSize: '13px', lineHeight: '1.6', margin: 0 },
  textarea: { backgroundColor: '#0F172A', color: '#F1F5F9', padding: '16px', borderRadius: '12px', fontSize: '15px', border: '1px solid #1E293B', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  lengths: { display: 'flex', gap: '8px' },
  lengthButton: { flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #1E293B', backgroundColor: '#0F172A', cursor: 'pointer' },
  lengthActive: { backgroundColor: '#3B82F6', borderColor: '#3B82F6' },
  disclaimerBox: { backgroundColor: '#0F172A', borderRadius: '12px', padding: '16px', border: '1px solid #1E293B' },
  disclaimerTitle: { color: '#F59E0B', fontSize: '11px', fontWeight: '700', letterSpacing: '2px', margin: '0 0 6px 0' },
  disclaimerText: { color: '#475569', fontSize: '13px', lineHeight: '1.6', margin: 0 },
  button: { backgroundColor: '#3B82F6', color: '#ffffff', padding: '20px', borderRadius: '12px', fontSize: '17px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
};